import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Button } from "antd"
import { ReloadOutlined } from "@ant-design/icons"
import { IconFolderSearch } from "@tabler/icons-react"
import type React from "react"
import { SetSearchLogFilterForm } from "./components/SetSearchLogFilterForm"
import { SetSearchLogTable } from "./components/SetSearchLogTable"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"
import { setSearchLogService, type SetSearchLogQueryParams } from "@/services/log/setSearchLogApi"

export interface SetSearchLogTableQueryParams extends SetSearchLogQueryParams {
  page: number
  per_page: number
}

export const SetSearchLogPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<SetSearchLogTableQueryParams>({
    ...PAGE_CONFIG,
    action: undefined,
    has_results: undefined,
    user_search: undefined,
  })

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["set_search_logs_list", searchParams],
    queryFn: () =>
      setSearchLogService.list({
        page: searchParams.page,
        limit: searchParams.per_page,
        action: searchParams.action || undefined,
        has_results: searchParams.has_results || undefined,
        user_search: searchParams.user_search || undefined,
      }),
  })

  const handleSearch = (values: SetSearchLogQueryParams) => {
    setSearchParams({
      ...searchParams,
      ...values,
      page: 1,
    })
  }

  const items = data?.data || []
  const totalCount = data?.total ?? 0

  return (
    <div className="flex flex-col gap-2">
      {/* Form bộ lọc & tìm kiếm */}
      <SetSearchLogFilterForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          action: searchParams.action,
          has_results: searchParams.has_results,
          user_search: searchParams.user_search,
        }}
      />

      {/* Bảng danh sách */}
      <Card
        className="flex min-h-0 grow flex-col shadow-xs"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              <IconFolderSearch className="text-blue-500" size={20} />
              <span className="font-semibold text-base text-gray-800">Log Tìm Kiếm Bộ Từ (Set Search Logs)</span>
              <Divider orientation="vertical" />
              <span className="font-normal text-gray-500 text-sm">
                Tổng số lượt tìm: <strong className="text-gray-700">{totalCount}</strong>
              </span>
            </div>
            <Button icon={<ReloadOutlined spin={isFetching} />} onClick={() => refetch()} loading={isFetching}>
              Làm mới
            </Button>
          </div>
        }
      >
        <SetSearchLogTable
          data={items}
          loading={isFetching}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total: totalCount,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>
    </div>
  )
}

export default SetSearchLogPage
